// Seasonal sale campaign (Black Friday / Holiday) — ONE config, pure logic.
//
// Why this exists: the 2025 sale was switched on by three unrelated things — a
// backend category name ("Black Friday Deals" / "Holiday Sale") string-matched in
// the shop, hardcoded launch/end dates in AnnouncementBar, and hand-placed CMS
// sections. Since the catalog migration (regionData joins backend offers ONTO the
// dataset `shopItem` docs) the backend category and `timeLimit` no longer reach
// the UI at all, so that wiring cannot be reused.
//
// The campaign is now a single dataset doc (`_id: "sale-campaign"`, `_type:
// "saleCampaign"`): dates, which items are in the sale, the limited-stock deals
// and the copy. Everything that shows the sale — announcement bar, home hero,
// /<region>/<route>, shop cards, product pages — reads it through these
// helpers, so moving a date is a one-line content change.
//
// PRICES ARE NOT HERE. What a customer pays still comes only from the backend
// offer (`discountPrice`). The campaign only decides what is LABELLED as a deal
// and when the sale ends. Ops must schedule the matching backend prices.
//
// Every sale item has one price for the whole sale, published up front.
// `limitedDeals` are a published price and a real number of units, live from
// the start until the units are gone or the deal ends (its own `end`, else the
// sale end; see getLimitedDealState and dealEndMs). VRS November 2026 Sales
// runs only one: Throttle & Brake pedals, for one week.
//
// Isomorphic + pure (no fs, no React): imported by the server data layer and by
// the client islands alike. Every time-dependent function takes `nowMs`
// explicitly so SSR and the client's first render agree (see useSaleClock).

export const SALE_CAMPAIGN_DOC_ID = 'sale-campaign'

// The homepage sale banner (saleHero, variant "home") is a seasonal strip that
// renders nothing outside the campaign and titles itself with an <h2>. It must
// never take the page-hero slot (RenderSections `isPageHero`), or the real hero
// below it loses the page's one <h1>.
export const isSeasonalBannerSection = (section) =>
  !!section && section._type === 'saleHero' && section.variant === 'home'

// Section _types that receive the campaign as props (injected server-side in
// lib/regionPageData.js so client and RSC paths both get it).
export const SALE_SECTION_TYPES = new Set([
  'saleHero',
  'saleLimitedDeals',
  'saleDealsGrid',
  'saleInfo',
  'shop',
  'productCheckoutPage',
])

export const PHASE_NONE = 'none'
export const PHASE_TEASER = 'teaser'
export const PHASE_LIVE = 'live'
export const PHASE_ENDED = 'ended'

const toMs = (v) => {
  if (v === null || v === undefined || v === '') return NaN
  if (typeof v === 'number') return v
  return Date.parse(v)
}

// Fields the client needs. Anything else on the doc (notes for ops, preview
// data) is dropped before the campaign is serialized into page props.
const PUBLIC_FIELDS = [
  'key', 'name', 'shortName', 'teaserStart', 'start', 'end', 'route', 'itemTypes',
  'limitedDeals', 'cardTag', 'limitedCardTag', 'limitedCardSub', 'popup', 'bar', 'hero', 'home', 'shopBanner',
  'limited', 'grid', 'product', 'regions', 'bonus', 'dealGroups',
]

/**
 * Normalize a raw campaign doc into the public, serializable shape. Returns null
 * when the doc is missing, disabled, or has unusable dates.
 */
export function normalizeCampaign(doc) {
  if (!doc || doc.enabled === false) return null
  const start = toMs(doc.start)
  const end = toMs(doc.end)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null
  const out = {}
  for (const k of PUBLIC_FIELDS) {
    if (doc[k] !== undefined) out[k] = doc[k]
  }
  out.route = doc.route || 'sale'
  out.itemTypes = Array.isArray(doc.itemTypes) ? doc.itemTypes.filter(Boolean) : []
  out.limitedDeals = (Array.isArray(doc.limitedDeals) ? doc.limitedDeals : []).filter((d) => d && d.itemType)
  return out
}

/** teaser (before start, after teaserStart) | live | ended | none. */
export function getCampaignPhase(campaign, nowMs) {
  if (!campaign) return PHASE_NONE
  const start = toMs(campaign.start)
  const end = toMs(campaign.end)
  const teaserStart = toMs(campaign.teaserStart)
  if (nowMs >= end) return PHASE_ENDED
  if (nowMs >= start) return PHASE_LIVE
  if (Number.isFinite(teaserStart) && nowMs >= teaserStart) return PHASE_TEASER
  return PHASE_NONE
}

export const isCampaignVisible = (campaign, nowMs) => {
  const p = getCampaignPhase(campaign, nowMs)
  return p === PHASE_TEASER || p === PHASE_LIVE
}

// A limited deal applies to a region when it lists no regions or lists this one.
const dealInRegion = (deal, region) =>
  !region || !Array.isArray(deal.regions) || deal.regions.length === 0 || deal.regions.includes(region)

// A limited deal covers its main `itemType` plus `alsoItemTypes`.
export const dealCoversItem = (deal, itemType) =>
  !!deal && !!itemType && (deal.itemType === itemType || (Array.isArray(deal.alsoItemTypes) && deal.alsoItemTypes.includes(itemType)))

/**
 * Limited-stock deals offered in `region`, in doc order. With `nowMs`, deals
 * whose own window has already ended are left out (each deal can run on its
 * own dates inside the sale: `start` / `end`, else the sale's).
 */
export function getLimitedDeals(campaign, region, nowMs) {
  if (!campaign) return []
  return campaign.limitedDeals.filter((d) => dealInRegion(d, region) && (!Number.isFinite(nowMs) || nowMs < dealEndMs(campaign, d)))
}

/** The published deal price for `region` as a backend-shaped price object, or null. */
export function dealPriceOf(deal, region, currency) {
  const v = deal && deal.prices && region ? deal.prices[region] : null
  return Number.isFinite(v) ? { priceCents: Math.round(v * 100), currency } : null
}

export const DEAL_UPCOMING = 'upcoming' // teaser: announced, not on sale yet
export const DEAL_LIVE = 'live' // backend charges the deal price
export const DEAL_PENDING = 'pending' // sale is live but the backend price is not loaded yet
export const DEAL_SOLD_OUT = 'sold-out' // units gone, item is back at its sale (or regular) price

/**
 * Where a limited deal stands for one offer. Units are counted by ops, not by
 * the site, so "sold out" has two triggers and either is enough:
 *  - `deal.soldOut: true` in the campaign doc (manual switch), or
 *  - the backend price is ABOVE the published deal price while still
 *    discounted, i.e. ops already moved it back to the sale price.
 * The second means the page flips by itself the moment ops change the price,
 * with no website deploy.
 */
export function getLimitedDealState(campaign, nowMs, region, deal, item) {
  const phase = getCampaignPhase(campaign, nowMs)
  if (!deal || (phase !== PHASE_LIVE && phase !== PHASE_TEASER)) return null
  if (nowMs >= dealEndMs(campaign, deal)) return null
  if (phase === PHASE_TEASER || nowMs < dealStartMs(campaign, deal)) return DEAL_UPCOMING
  if (deal.soldOut === true) return DEAL_SOLD_OUT
  if (!item || !hasBackendDiscount(item)) return DEAL_PENDING
  const target = dealPriceOf(deal, region)
  if (target && item.discountPrice.priceCents > target.priceCents) return DEAL_SOLD_OUT
  return DEAL_LIVE
}

/** When a limited deal starts: its own `start`, else the start of the sale. */
export function dealStartMs(campaign, deal) {
  const own = toMs(deal && deal.start)
  return Number.isFinite(own) ? own : toMs(campaign && campaign.start)
}

/** Is this limited deal on sale at `nowMs` (inside its own window)? */
export const isDealRunning = (campaign, deal, nowMs) =>
  !!deal && nowMs >= dealStartMs(campaign, deal) && nowMs < dealEndMs(campaign, deal)

/** When a limited deal stops: its own `end`, else the end of the sale. */
export function dealEndMs(campaign, deal) {
  const own = toMs(deal && deal.end)
  return Number.isFinite(own) ? own : toMs(campaign && campaign.end)
}

/**
 * Limited deals in separate sets with their own timer (e.g. the pedal deals and
 * the wheel deals): `campaign.dealGroups` = [{ key, title, countdownLive,
 * countdownTeaser, images, byRegion }] in display order, each deal names its set in `group`
 * (none = one set for all). A set runs from its first deal's start to its last
 * deal's end; ended sets are left out. Returns, in order:
 * { key, title, countdownLive, countdownTeaser, images, startMs, endMs, live, targetMs, deals }
 * where `live` is false while the set is still to come and `targetMs` is what
 * its timer counts down to (the start, else the end).
 */
export function getDealGroups(campaign, nowMs, region) {
  if (!campaign) return []
  const phase = getCampaignPhase(campaign, nowMs)
  if (phase !== PHASE_LIVE && phase !== PHASE_TEASER) return []
  const meta = Array.isArray(campaign.dealGroups) ? campaign.dealGroups.filter((g) => g && g.key) : []
  const deals = getLimitedDeals(campaign, region, nowMs)
  const keyOf = (d) => d.group || 'deals'
  const keys = [...meta.map((g) => g.key), ...deals.map(keyOf)].filter((k, i, a) => a.indexOf(k) === i)
  return keys.map((key) => {
    const own = deals.filter((d) => keyOf(d) === key)
    if (!own.length) return null
    const startMs = Math.min(...own.map((d) => dealStartMs(campaign, d)))
    const endMs = Math.max(...own.map((d) => dealEndMs(campaign, d)))
    const live = phase === PHASE_LIVE && nowMs >= startMs
    const base = meta.find((g) => g.key === key) || {}
    // byRegion: copy for a store where the set differs (e.g. no Lite wheel in the US)
    const m = { ...base, ...((base.byRegion && region && base.byRegion[region]) || {}) }
    return {
      key,
      title: m.title || null,
      short: m.short || m.title || null,
      countdownLive: m.countdownLive || null,
      images: Array.isArray(m.images) ? m.images.filter((i) => i && i.src) : [],
      countdownTeaser: m.countdownTeaser || null,
      startMs,
      endMs,
      live,
      targetMs: live ? endMs : startMs,
      deals: own,
    }
  }).filter(Boolean)
}

/** The limited deal covering this item in `region`, or null. */
export function getLimitedDealForItem(campaign, region, itemType) {
  if (!itemType) return null
  return getLimitedDeals(campaign, region).find((d) => dealCoversItem(d, itemType)) || null
}

/** Is this item part of the sale right now (live phase, listed or a limited deal)? */
export function isItemInSale(campaign, nowMs, region, itemType) {
  if (!campaign || !itemType || getCampaignPhase(campaign, nowMs) !== PHASE_LIVE) return false
  if (campaign.itemTypes.includes(itemType)) return true
  return isDealRunning(campaign, getLimitedDealForItem(campaign, region, itemType), nowMs)
}

/** The itemType string of a backend offer (itemType may be a nested object). */
export function offerItemType(item) {
  if (!item) return null
  return typeof item.itemType === 'object' && item.itemType ? item.itemType.itemType : item.itemType || item.type
}

/**
 * The pill a shop card should show for this item, or null:
 * limited deal still running -> campaign.limitedCardTag ("Limited time offer")
 * plus `sub` (limitedCardSub, "or until stock runs out") and `endsAtMs` for the
 * card countdown; anything else in the sale -> cardTag.
 * Only when the backend actually prices it below regular, so a label never
 * promises a discount the checkout will not give.
 */
export function getSaleCardTag(campaign, nowMs, region, item) {
  if (!item) return null
  const itemType = offerItemType(item)
  if (!isItemInSale(campaign, nowMs, region, itemType)) return null
  if (!hasBackendDiscount(item)) return null
  const deal = getLimitedDealForItem(campaign, region, itemType)
  if (deal && getLimitedDealState(campaign, nowMs, region, deal, item) === DEAL_LIVE) {
    return {
      label: campaign.limitedCardTag || 'Limited stock deal',
      sub: campaign.limitedCardSub || null,
      variant: 'sale-limited',
      endsAtMs: dealEndMs(campaign, deal),
    }
  }
  return { label: campaign.cardTag || campaign.shortName || 'Sale', variant: 'sale' }
}

/**
 * The free extra that comes with a purchase during the sale (e.g. 3 months of
 * VRS Competitive), or null. Shown as added value next to the price, never
 * folded into it. campaign.bonus = { label, worth: { us, eu, au, world },
 * allItems }: allItems true = every product while the sale is live, otherwise
 * only the items in the sale. `worth` is a display string per region.
 */
export function getSaleBonus(campaign, nowMs, region, itemType) {
  const b = campaign && campaign.bonus
  if (!b || !b.label || getCampaignPhase(campaign, nowMs) !== PHASE_LIVE) return null
  if (!b.allItems && !isItemInSale(campaign, nowMs, region, itemType)) return null
  const worth = (b.worth && (b.worth[region] || b.worth.world)) || null
  return { label: b.label, short: b.pillLabel || b.label, worth }
}

/** True when the backend offer is priced below its regular price. */
export function hasBackendDiscount(item) {
  if (!item) return false
  const d = item.discountPrice && item.discountPrice.priceCents
  const r = (item.regularPrice && item.regularPrice.priceCents) || (item.price && item.price.priceCents)
  return Number.isFinite(d) && Number.isFinite(r) && d < r
}

/** Remaining time split for a countdown. Never negative. */
export function splitRemaining(targetMs, nowMs) {
  const total = Math.max(0, (Number(targetMs) || 0) - nowMs)
  return {
    total,
    days: Math.floor(total / 86400000),
    hours: Math.floor((total % 86400000) / 3600000),
    minutes: Math.floor((total % 3600000) / 60000),
    seconds: Math.floor((total % 60000) / 1000),
  }
}

export const msOf = toMs

/**
 * Server-side: attach the normalized campaign + the server clock to every sale-
 * aware section in a loaded page tree, IN PLACE. `clockOffsetMs` lets a preview
 * environment pretend it is another date (SALE_PREVIEW_NOW); it is 0 in prod.
 */
export function injectSaleCampaign(node, campaign, nowMs, clockOffsetMs = 0, seen = new Set()) {
  if (!node || typeof node !== 'object' || seen.has(node)) return
  seen.add(node)
  if (Array.isArray(node)) {
    node.forEach((n) => injectSaleCampaign(n, campaign, nowMs, clockOffsetMs, seen))
    return
  }
  if (typeof node._type === 'string' && SALE_SECTION_TYPES.has(node._type)) {
    node.saleCampaign = campaign || null
    node.saleNowMs = nowMs
    node.saleClockOffsetMs = clockOffsetMs
  }
  for (const k of Object.keys(node)) {
    const v = node[k]
    if (v && typeof v === 'object' && k !== 'saleCampaign') injectSaleCampaign(v, campaign, nowMs, clockOffsetMs, seen)
  }
}

/**
 * Preview clock (server-only callers). SALE_PREVIEW_NOW=<ISO> makes a preview
 * box render the sale as if it were that moment; the offset is carried to the
 * client so countdowns tick from the same pretend time. Ignored in production
 * builds unless SALE_PREVIEW_ALLOW_PROD=1 is also set (never on vrs.racing).
 */
export function getSaleClock(env = (typeof process !== 'undefined' ? process.env : {}), realNowMs = Date.now()) {
  const preview = env && env.SALE_PREVIEW_NOW
  const allowed = env && (env.NODE_ENV !== 'production' || env.SALE_PREVIEW_ALLOW_PROD === '1')
  if (preview && allowed) {
    const p = Date.parse(preview)
    if (Number.isFinite(p)) return { nowMs: p, clockOffsetMs: p - realNowMs }
  }
  return { nowMs: realNowMs, clockOffsetMs: 0 }
}
